import { GuideScreenshot } from '@/components/guide-screenshot';

export const meta = {
  title: 'WordCraft App 入门指南：从首次打开到完成一份文档',
  description:
    '通过一份项目简报学会 WordCraft：选择桌面或网页版本、使用标题样式、制作表格、检查修订，以及保存并验证文档。',
  updated_at: '2026-11-05',
  guide: true,
  image: '/imgs/wordcraft/hero',
  imageAlt: 'WordCraft 开始选项卡、样式库与文档导航',
  category: '新手入门',
  readingMinutes: 10,
  toc: [
    { id: 'what-is-wordcraft', title: '认识这款应用' },
    { id: 'choose-your-version', title: '选择桌面版或网页版' },
    { id: 'first-document', title: '创建第一份文档' },
    { id: 'structure-and-style', title: '组织结构与应用样式' },
    { id: 'tables-and-layout', title: '添加表格与检查版面' },
    { id: 'review-and-save', title: '审阅、保存与重新打开' },
    { id: 'first-week', title: '建立第一周的练习习惯' },
    { id: 'questions', title: '常见问题' },
  ],
};

学习 WordCraft App，最有效的起点是完成一份真正能交给别人阅读的小文档。与复杂简历或长篇论文相比，一页项目简报更适合初次练习：它会自然地用到标题、正文、列表、表格和文件保存，又不会一开始就带来大量格式问题。

这篇指南带你从选择版本一直做到重新打开保存结果。你可以安装桌面应用，也可以从 [WordCraft 首页](/)找到在线编辑器。第一次操作时不必追求速度，先建立一个可以重复执行的流程，再逐步熟悉功能区里的更多工具。

<h2 id="what-is-wordcraft">WordCraft App 是什么？</h2>

WordCraft 是 ArtCraft 开发的开源文字处理软件，使用熟悉的功能区组织写作、排版和审阅工具，支持 DOCX 文档，并提供桌面与浏览器版本。当前网站 WordCraftApp 是独立指南和在线使用入口，不是软件开发方。软件源码和官方说明可在[上游项目仓库](https://github.com/storytold/wordcraft)查看。

第一次使用时，可以把它理解为一个把信息整理成可阅读、可编辑文档的工作空间。标题负责表达结构，段落负责解释观点，表格负责比较重复信息。掌握这三个基本元素，比记住所有按钮的位置更有帮助。

适合练习的材料包括会议议程、读书笔记、活动计划和项目简报。请使用副本练习，保留导入文件的原件。如果你最关心的是它能否接替现有的 Word 工作流，可以先阅读 [DOCX 兼容性与选型指南](/wordcraft-guide)，用自己的文件进行判断。

<h2 id="choose-your-version">先决定使用桌面版还是网页版</h2>

希望不安装就先体验界面，可以打开[在线编辑器页面](/online)。准备经常在同一台电脑写作，则可以参考[桌面版下载指南](/download)。下载时留意发布日期和平台信息：网站托管的在线版本不一定与最新桌面版本同步。

选择版本时，要考虑实际工作环境。临时换电脑修改一份短文档，浏览器入口比较方便，但你需要明确如何把结果带走。经常断网工作，则应先验证安装版在自己的设备上能否满足需求。浏览器打开过一次，并不代表之后一定能离线正常启动。

开始写作前，在电脑上创建一个“WordCraft 练习”文件夹，把可编辑文件和导出结果都放进去。文件管理看起来不起眼，却能避免新手很常见的问题：文档已经写完，也确实保存过，但在下载目录里无法判断哪份才是最新版本。

<GuideScreenshot
  base="/imgs/wordcraft/backstage"
  alt="WordCraft 文件页面，显示空白文档、手册示例、信件、简历和报告模板"
  caption="图 1：上游软件的文件页面展示了几种文档起点。具体模板与文字可能随版本变化。图片来源：ArtCraft / WordCraft，MIT 或 Apache-2.0 许可。"
/>

<h2 id="first-document">用一个明确的任务创建第一份文档</h2>

先建立空白文档。如果你想了解示例的排版，可以单独打开示例，观察标题与正文的组织方式，再新建自己的文件。直接在很长的示例里东删一段、西改一行，容易留下不理解的格式设置。

这里以“社区自行车维修体验课”为例。分别输入文档标题、一句话目的，以及“参加对象”“活动安排”“下一步”三个章节名称。在每个章节下面写两三句话。活动目的可以写成：“用九十分钟帮助六位邻居学会检查胎压、调整座椅，并完成一次基础清洁。”

暂时保持文字朴素，先回答读者真正关心的问题：谁来参加、需要带什么、活动在哪里举行、结束后要做什么。内容尚未清楚时，不必急着挑选标题颜色。结构和信息量确定后，外观调整会更容易。

输入少量内容后就进行第一次保存，例如命名为 `workshop-brief-draft.docx`。在浏览器里，需要完成文件选择窗口，或者确认文件已经下载。取消保存窗口不等于后台自动保存。提前做这个动作，可以在投入大量写作之前发现权限或保存位置的问题。

<h2 id="structure-and-style">让标题样式承担文档结构</h2>

给文档名称应用 Title 或标题样式，给三个主要章节应用 Heading 1 或一级标题，解释性段落使用普通正文样式。章节名称应准确说明下面的内容。“参加者需要携带的物品”通常比“其他事项”更容易理解。

样式的价值在文档修改时尤其明显。如果每个章节标题都只是单独调大字号、手动加粗，那么统一修改外观时就需要逐个检查。使用一致的样式，可以把同一层级的内容作为一类来管理，也更容易快速查看整篇文档的提纲。

只有在章节真正需要细分时才使用二级标题。例如“活动安排”下面可以分为“准备工作”和“现场流程”。不要仅仅因为喜欢某一级标题的字号，就跳过中间层级。先决定信息之间的关系，再决定它们的外观。

查看开始选项卡、样式区域和导航区域，尝试在章节之间切换。如果某个标题没有出现在文档提纲里，检查它是否真正应用了标题样式，而不仅是把文字放大。需要了解各个选项卡时，可以继续查看[完整基础教程](/tutorials)。

<GuideScreenshot
  base="/imgs/wordcraft/hero"
  alt="WordCraft 开始选项卡，显示段落控制、样式库和导航窗格中的章节标题"
  caption="图 2：示例文档展示了标题样式与文档导航的联系。这里用于认识界面，不要求你的文档复制相同设计。图片来源：ArtCraft / WordCraft。"
/>

正文选择容易阅读的字号和适度段间距。段落之间的留白应优先通过段落设置控制，不要连续插入大量空行。物品清单使用真正的列表，不要用多个空格把文字“推”到某个位置；一旦字体或页面宽度改变，靠空格拼出的对齐往往会失效。

<h2 id="tables-and-layout">添加一张有用途的表格，再检查页面</h2>

在活动流程下面插入三列表格，分别填写“环节”“负责人”“分钟数”。行内容可以是开场、演示和练习。每个单元格尽量保持简洁，让组织者能快速找到自己负责的环节，而不是把整段说明塞进表格。

让表头与内容有明确区分，并检查列宽是否合理。如果活动名称反复换行，而分钟数一栏几乎全是空白，就重新分配宽度。表格过宽时，优先缩短表达或拆分信息；直接把所有文字缩得很小，通常只会让阅读更困难。

随后检查纸张大小、页边距和方向。使用收件人预期的纸型，因为 A4 与 Letter 的尺寸差异会影响分页。如果简报多出一页，先查看是否存在多余空行、过大的段落间距或可以精简的句子，再考虑缩小字号。

第一次练习可以暂时不加入浮动图片和复杂文本框。等基本版式稳定之后，再逐个尝试新对象。能够保存后稳定重开的简单文档，比外观复杂但稍改一句就错位的文档更有用。[在线写作指南](/wordcraft-online-guide)提供了从草稿到交付的完整练习。

<h2 id="review-and-save">先审阅内容，再证明保存成功</h2>

从作者角度通读一遍，核对名称、日期、地点和操作要求；再从收件人角度读一遍，确认几秒钟内就能找到活动时间、需要携带的物品和下一步行动。拼写检查不能替你发现“忘记写集合地点”这样的信息缺口。

想体验批注和修订时，先复制练习文件。添加一条批注，例如“下雨时是否更换场地”，再做一个明显的文字修改，观察标记如何显示，并尝试接受或拒绝这一处修改。单个清楚的例子，比一开始处理满页修订更容易理解。

<GuideScreenshot
  base="/imgs/wordcraft/review"
  alt="WordCraft 审阅选项卡，文档旁显示批注与修订标记"
  caption="图 3：审阅界面展示批注与修改标记。与其他软件交换审阅文件时，还需要检查保存后的实际 DOCX。图片来源：ArtCraft / WordCraft。"
/>

再次保存可编辑 DOCX。如果收件人只需要阅读或打印，可以通过当前版本提供的导出选项制作独立 PDF。保留 DOCX 作为后续修改的源文件，避免下次只能从 PDF 重新整理内容。

不要在看到文件出现在文件夹里后就结束。重新打开保存副本，查看标题、最后一句、表格和需要保留的批注。导出 PDF 时，逐页检查，并留意是否出现多余的空白末页。文件存在只能证明产生了一个文件，重新打开才能验证它是不是你想要的结果。

<h2 id="first-week">把练习变成一个可重复的习惯</h2>

第一周可以依次完成三种小文档：会议议程、两页笔记和简单报告。重复“提纲、草稿、样式、审阅、保存、重开”的顺序，每次只增加一个新功能，先让基本过程稳定下来。

在练习文件夹旁放一份简短检查清单：文件名明确、标题层级一致、纸张大小正确、保存副本已验证。遇到问题时记录具体版本和操作步骤。“这个表格在保存重开后多出一页”比“软件有问题”更容易帮助你定位原因。

练习完成后，再用真实文件的副本尝试迁移。先从结构简单的文件开始，确认正常后再逐渐加入长表格、图片和多层章节。这样得到的是对自己工作材料的认识，而不是对“兼容性很好”这类笼统说法的信任。

<h2 id="questions">新手常见问题</h2>

### 必须安装软件才能跟着练习吗？

不必，可以从[在线页面](/online)开始。浏览器环境下，建议把保存并重新打开短文档作为第一项练习。桌面安装是另一种选择，下载页面提供平台入口。

### WordCraftApp 是官方开发方吗？

不是。软件由 ArtCraft 开发，本站提供独立教程与在线入口。版本发布、源码和开发进度请以官方项目链接为准。

### 初次使用应该选模板还是空白文档？

空白文档更容易理解，因为每个样式和对象都是自己添加的。认识基本结构之后，再使用模板会更容易判断哪些部分应该保留、删除或调整。

### 文档换个软件打开后版式不同怎么办？

先检查字体、纸型、页边距、表格列宽和浮动对象。参考[兼容性指南](/wordcraft-guide)进行有控制的对比，在收件人确认结果之前保留原件和修改副本。

### 编辑器空白或找不到保存文件怎么办？

阅读[网页版排障指南](/wordcraft-web-version-guide)，按照启动、权限、字体和保存位置分别检查。不要把所有异常都当成同一个问题反复刷新。
